import type { OperationalForecastDto, ProductMaterialBreakdownDto, ProductMonthDto } from './product-analytics-storage'
import { dampedHoltTrend } from './damped-holt-trend'

export enum ForecastHorizon {
  YearEnd = 'year_end',
  TwelveMonths = 'twelve_months',
}

export enum ForecastProfile {
  Flat = 'flat',
  Seasonal = 'seasonal',
}

export enum ForecastMetric {
  Turnover = 'turnover',
  Reinvestment = 'reinvestment',
}

export type ForecastPointBasis = 'operational' | 'market_benchmark'

export interface ReinvestmentForecastPoint {
  month: string
  basis: ForecastPointBasis
  flatTurnoverKopecks: number
  baselineTurnoverKopecks: number
  scenarioTurnoverKopecks: number
  lowTurnoverKopecks: number
  highTurnoverKopecks: number
  reinvestedThisMonthKopecks: number
  cumulativeReinvestmentKopecks: number
  upliftRate: number
}

export interface NextMonthSeasonalBaseline {
  month: string
  turnoverKopecks: number
  incomeKopecks: number
}

export interface ReinvestmentForecastAvailable {
  availability: 'available'
  validation: 'not_enough_history'
  points: ReinvestmentForecastPoint[]
  ownHistory: ProductMonthDto[]
  directionalAgreement: { matched: number; compared: number } | null
  materialMix: { gold: number; silver: number }
  maximumUpliftRate: number
}

export interface ReinvestmentForecastUnavailable {
  availability: 'unavailable'
  reason: 'operational_forecast_missing' | 'refund_baseline_missing' | 'income_estimate_missing'
}

export type ReinvestmentForecast = ReinvestmentForecastAvailable | ReinvestmentForecastUnavailable

/**
 * Official 2025 Russian retail unit volumes, derived from the cumulative
 * monthly charts published by the Federal Assay Chamber. These are a
 * directional category benchmark, not Almaz revenue or marketplace demand.
 */
export const RUSSIAN_JEWELRY_RETAIL_2025_UNITS = {
  gold: [1.6, 1.5, 1.9, 1.4, 1.2, 1.4, 1.5, 1.6, 1.3, 1.5, 1.4, 2.5],
  silver: [4, 4, 4.6, 3.2, 3, 3.7, 3.8, 4.2, 3.6, 3.7, 4.2, 6.6],
} as const

const MAXIMUM_REINVESTMENT_UPLIFT_RATE = 0.35
const RESPONSE_SCALE_IN_ANCHOR_MONTHS = 1.5

function safeInteger(value: number): number {
  return Math.min(Number.MAX_SAFE_INTEGER, Math.max(0, Math.round(value)))
}

function addMonths(month: string, offset: number): string {
  const [year, monthNumber] = month.split('-').map(Number)
  const date = new Date(Date.UTC(year, monthNumber - 1 + offset, 1))
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`
}

export function nextMonthSeasonalBaseline(
  operational: OperationalForecastDto | null,
  materials: readonly ProductMaterialBreakdownDto[],
  monthly: readonly ProductMonthDto[],
): NextMonthSeasonalBaseline | null {
  if (!operational || operational.estimateKopecks <= 0 || operational.estimateIncomeKopecks === null) return null
  const indexes = jewelrySeasonalIndexes(materials).indexes
  const targetIndex = indexes[Number(operational.targetMonth.slice(5, 7)) - 1] || 1
  const month = addMonths(operational.targetMonth, 1)
  const nextIndex = indexes[Number(month.slice(5, 7)) - 1] || 1
  const ratio = nextIndex / targetIndex
  const allCompleted = monthly.filter(point => point.month < operational.targetMonth)
  const completed = allCompleted.slice(-6)
  const previousYearMonth = addMonths(month, -12)
  const previousYear = allCompleted.find(point => point.month === previousYearMonth)
  const median = (values: readonly number[]) => {
    const sorted = [...values].sort((left, right) => left - right)
    if (!sorted.length) return null
    const middle = Math.floor(sorted.length / 2)
    return sorted.length % 2 === 0 ? (sorted[middle - 1] + sorted[middle]) / 2 : sorted[middle]
  }
  const robustRecent = (value: (point: ProductMonthDto) => number) => median(completed.map(point => {
    const sourceIndex = indexes[Number(point.month.slice(5, 7)) - 1] || 1
    return value(point) * nextIndex / sourceIndex
  }))
  const blend = (currentValue: number, value: (point: ProductMonthDto) => number) => {
    const currentSeasonal = currentValue * ratio
    const recent = robustRecent(value)
    if (recent === null || recent <= 0) return safeInteger(currentSeasonal)
    const adjustedHistory = completed.map(point => {
      const sourceIndex = indexes[Number(point.month.slice(5, 7)) - 1] || 1
      return value(point) / sourceIndex
    })
    const lastCompleted = completed.at(-1)?.month
    const monthOrdinal = (key: string) => Number(key.slice(0, 4)) * 12 + Number(key.slice(5, 7)) - 1
    const projectionSteps = lastCompleted ? Math.max(1, monthOrdinal(month) - monthOrdinal(lastCompleted)) : 1
    const adjustedTrend = adjustedHistory.length
      ? dampedHoltTrend(adjustedHistory, projectionSteps).at(-1) || 0
      : 0
    const seasonalTrend = adjustedTrend * nextIndex
    const anchor = median([recent, seasonalTrend].filter(candidate => candidate > 0)) || recent
    const boundedCurrent = Math.min(anchor * 1.5, Math.max(anchor * 0.65, currentSeasonal))
    const yearAgo = previousYear ? value(previousYear) : null
    return safeInteger(yearAgo === null
      ? boundedCurrent * 0.45 + seasonalTrend * 0.35 + recent * 0.2
      : boundedCurrent * 0.4 + seasonalTrend * 0.25 + recent * 0.2 + yearAgo * 0.15)
  }
  return {
    month,
    turnoverKopecks: blend(operational.estimateKopecks, point => point.turnoverKopecks),
    incomeKopecks: blend(operational.estimateIncomeKopecks, point => point.incomeKopecks ?? 0),
  }
}

function horizonLength(targetMonth: string, horizon: ForecastHorizon): number {
  if (horizon === ForecastHorizon.TwelveMonths) return 12
  const month = Number(targetMonth.slice(5, 7))
  return Math.max(1, 13 - month)
}

function normalize(values: readonly number[]): number[] {
  const mean = values.reduce((sum, value) => sum + value, 0) / values.length
  return values.map((value) => value / mean)
}

function materialMix(materials: readonly ProductMaterialBreakdownDto[]): { gold: number; silver: number } {
  const gold = materials.find((item) => item.material === 'gold')?.revenueKopecks || 0
  const silver = materials.find((item) => item.material === 'silver')?.revenueKopecks || 0
  const total = gold + silver
  return total > 0 ? { gold: gold / total, silver: silver / total } : { gold: 0.5, silver: 0.5 }
}

export function jewelrySeasonalIndexes(materials: readonly ProductMaterialBreakdownDto[]): {
  indexes: number[]
  mix: { gold: number; silver: number }
} {
  const mix = materialMix(materials)
  const gold = normalize(RUSSIAN_JEWELRY_RETAIL_2025_UNITS.gold)
  const silver = normalize(RUSSIAN_JEWELRY_RETAIL_2025_UNITS.silver)
  return {
    indexes: gold.map((value, index) => value * mix.gold + silver[index] * mix.silver),
    mix,
  }
}

function direction(value: number): -1 | 0 | 1 {
  if (Math.abs(value) < 0.03) return 0
  return value > 0 ? 1 : -1
}

function directionalAgreement(history: readonly ProductMonthDto[], indexes: readonly number[]): {
  matched: number
  compared: number
} | null {
  const points = history.filter((point) => point.turnoverKopecks > 0).slice(-4)
  if (points.length < 3) return null
  let matched = 0
  let compared = 0
  for (let index = 1; index < points.length; index += 1) {
    const previous = points[index - 1]
    const current = points[index]
    const ownDirection = direction(current.turnoverKopecks / previous.turnoverKopecks - 1)
    const previousBenchmark = indexes[Number(previous.month.slice(5, 7)) - 1]
    const currentBenchmark = indexes[Number(current.month.slice(5, 7)) - 1]
    const marketDirection = direction(currentBenchmark / previousBenchmark - 1)
    if (ownDirection === 0 || marketDirection === 0) continue
    compared += 1
    if (ownDirection === marketDirection) matched += 1
  }
  return compared > 0 ? { matched, compared } : null
}

export function buildReinvestmentForecast(input: {
  operational: OperationalForecastDto | null
  monthly: readonly ProductMonthDto[]
  materials: readonly ProductMaterialBreakdownDto[]
  refundRate: number | null
  reinvestmentShare: number
  horizon: ForecastHorizon
  profile: ForecastProfile
  includeReinvestmentResponse: boolean
}): ReinvestmentForecast {
  const { operational } = input
  if (!operational) return { availability: 'unavailable', reason: 'operational_forecast_missing' }
  if (input.refundRate === null) return { availability: 'unavailable', reason: 'refund_baseline_missing' }
  if (
    operational.incomeEstimate.availability === 'unavailable'
    || operational.estimateIncomeKopecks === null
  ) {
    return { availability: 'unavailable', reason: 'income_estimate_missing' }
  }
  if (operational.estimateKopecks <= 0) {
    return { availability: 'unavailable', reason: 'operational_forecast_missing' }
  }

  const { indexes, mix } = jewelrySeasonalIndexes(input.materials)
  const targetIndex = indexes[Number(operational.targetMonth.slice(5, 7)) - 1]
  const normalizedLevel = operational.estimateKopecks / targetIndex
  const incomeRate = operational.estimateIncomeKopecks / operational.estimateKopecks
  const share = Math.min(1, Math.max(0, input.reinvestmentShare))
  const refundRate = Math.min(1, Math.max(0, input.refundRate))
  const responseScale = Math.max(operational.estimateKopecks * RESPONSE_SCALE_IN_ANCHOR_MONTHS, 1)
  const count = horizonLength(operational.targetMonth, input.horizon)
  let cumulativeReinvestment = 0
  const points: ReinvestmentForecastPoint[] = []

  for (let index = 0; index < count; index += 1) {
    const month = addMonths(operational.targetMonth, index)
    const monthIndex = indexes[Number(month.slice(5, 7)) - 1]
    const seasonalRatio = input.profile === ForecastProfile.Seasonal ? monthIndex / targetIndex : 1
    const baseline = index === 0
      ? operational.estimateKopecks
      : normalizedLevel * (input.profile === ForecastProfile.Seasonal ? monthIndex : targetIndex)
    const flat = operational.estimateKopecks
    const low = index === 0 ? operational.lowKopecks : operational.lowKopecks * seasonalRatio
    const high = index === 0 ? operational.highKopecks : operational.highKopecks * seasonalRatio
    const upliftRate = input.includeReinvestmentResponse && index > 0
      ? MAXIMUM_REINVESTMENT_UPLIFT_RATE * (1 - Math.exp(-cumulativeReinvestment / responseScale))
      : 0
    const scenario = baseline * (1 + upliftRate)
    const reinvestedThisMonth = scenario * incomeRate * (1 - refundRate) * share
    cumulativeReinvestment = safeInteger(cumulativeReinvestment + reinvestedThisMonth)
    points.push({
      month,
      basis: index === 0 ? 'operational' : 'market_benchmark',
      flatTurnoverKopecks: safeInteger(flat),
      baselineTurnoverKopecks: safeInteger(baseline),
      scenarioTurnoverKopecks: safeInteger(scenario),
      lowTurnoverKopecks: safeInteger(low * (1 + upliftRate)),
      highTurnoverKopecks: safeInteger(high * (1 + upliftRate)),
      reinvestedThisMonthKopecks: safeInteger(reinvestedThisMonth),
      cumulativeReinvestmentKopecks: cumulativeReinvestment,
      upliftRate,
    })
  }

  const ownHistory = input.monthly
    .filter((point) => point.month < operational.targetMonth && point.turnoverKopecks > 0)
    .slice(-4)

  return {
    availability: 'available',
    validation: 'not_enough_history',
    points,
    ownHistory,
    directionalAgreement: directionalAgreement(ownHistory, indexes),
    materialMix: mix,
    maximumUpliftRate: MAXIMUM_REINVESTMENT_UPLIFT_RATE,
  }
}
